import { use, useEffect, useRef, useState, type RefObject } from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import {
  QueryViewerContext,
  useDiscardUnavailableData,
} from "@softmax/web-features/data";
import { useBrowserSession } from "@softmax/web-features/session";
import { credentialMutationOptions } from "./credentials.mutations";
import { credentialsQueryOptions } from "./credentials.queries";
import { useCredentialCopy } from "./copy/use-credential-copy";
import { isUncertainWrite } from "../write-failure";
import type { CredentialIntent } from "./credential-intent";

export function useCredentialOperation({
  intent,
  finalFocusRef,
  onClose,
}: {
  readonly intent: CredentialIntent;
  readonly finalFocusRef: RefObject<HTMLElement | null>;
  readonly onClose: () => void;
}) {
  const viewerId = use(QueryViewerContext);
  const session = useBrowserSession();
  const authorized = session.kind === "authenticated";
  const queryClient = useQueryClient();
  const [name, setName] = useState("");
  const [allowWrite, setAllowWrite] = useState(true);
  const [token, setToken] = useState<string | null>(null);
  const submitted = useRef(false);
  const copy = useCredentialCopy();
  const mutation = useMutation(
    credentialMutationOptions({
      queryClient,
      viewerId,
      target: intent.target,
      onCreated: setToken,
    }),
  );
  const uncertain = mutation.error !== null && isUncertainWrite(mutation.error);
  const options = credentialsQueryOptions({ viewerId, target: intent.target });
  const query = useQuery(options);
  const unavailable = useDiscardUnavailableData({
    queryKey: options.queryKey,
    error: query.error,
  });
  useEffect(() => {
    if (unavailable) onClose();
  }, [unavailable, onClose]);

  function close() {
    if (!submitted.current && !mutation.isPending) {
      copy.reset();
      setToken(null);
      onClose();
    }
  }

  async function refresh() {
    if (query.isFetching) return;

    const result = await query.refetch();

    if (
      result.isSuccess &&
      intent.action === "revoke" &&
      !result.data.some((row) => row.id === intent.record.id)
    ) {
      finalFocusRef.current = intent.successFocusRef.current;
    }
  }

  function submit() {
    if (submitted.current || mutation.isPending || uncertain || !authorized)
      return;

    if (intent.action === "create" && !name.trim()) return;

    submitted.current = true;
    mutation.mutate(
      intent.action === "revoke"
        ? { kind: "revoke", id: intent.record.id }
        : { kind: "create", name: name.trim(), allowWrite },
      {
        onSuccess: () => {
          if (intent.action === "revoke") {
            finalFocusRef.current = intent.successFocusRef.current;
            onClose();
          }
        },
        onSettled: () => {
          submitted.current = false;
        },
      },
    );
  }

  function changeName(next: string) {
    setName(next);

    if (!uncertain) mutation.reset();
  }

  function copyToken() {
    if (authorized && token) copy.copy({ token });
  }

  return {
    session,
    authorized,
    name,
    allowWrite,
    setAllowWrite,
    token,
    copy,
    mutation,
    uncertain,
    query,
    unavailable,
    close,
    refresh,
    submit,
    changeName,
    copyToken,
  };
}
